import LaunchIcon from '@mui/icons-material/Launch'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod/v4'
import { SettingsLayout, SettingsSection } from '~/settings/components/settings-layout'
import { formatDate } from '~/utils'

const SubscriptionStatus = z.enum([
    'incomplete',
    'incomplete_expired',
    'trialing',
    'active',
    'past_due',
    'canceled',
    'unpaid',
    'paused',
])

const SubscriptionSchema = z.object({
    id: z.number(),
    subscription_id: z.string(),
    plan_name: z.string().nullable(),
    start_date: z.coerce.date(),
    end_date: z.coerce.date().nullable(),
    canceled_date: z.coerce.date().nullable(),
    status: SubscriptionStatus,
    created_at: z.coerce.date(),
    last_updated_at: z.coerce.date(),
})
export type Subscription = z.infer<typeof SubscriptionSchema>

const SubscriptionsResponseSchema = z.array(SubscriptionSchema)

export type SubscriptionsResponse = z.infer<typeof SubscriptionsResponseSchema>

// TODO: Integrate with API call to /billing/subscriptions/ https://github.com/oakra-dev/oakra/pull/2121
async function getSubscriptions(): Promise<SubscriptionsResponse> {
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // Mock data matching the expected structure
    const mockResponse = [
        {
            id: 1,
            subscription_id: 'sub_1234567890',
            plan_name: 'Pro',
            start_date: '2025-01-01',
            end_date: '2025-07-30',
            canceled_date: null,
            status: 'active',
            created_at: '2025-01-01T00:00:00Z',
            last_updated_at: '2025-01-01T00:00:00Z',
        },
    ]

    const result = SubscriptionsResponseSchema.parse(mockResponse)

    return result
}

const useSubscriptions = () => {
    return useQuery({
        queryKey: ['subscriptions'],
        queryFn: async () => {
            const subs = await getSubscriptions()
            return subs
        },
    })
}

const PlanButton = styled(Button)({
    minWidth: 160,
    textTransform: 'none',
    fontWeight: 500,
})

const BillingButton = PlanButton

function ManagePlan() {
    const { data } = useSubscriptions()
    const latestSubscription = data?.at(0)

    const handleSwitchPlan = () => {
        console.log('switch plan')
    }

    const handleCancelPlan = () => {
        console.log('cancel plan')
    }

    return (
        <SettingsSection header={gettext('Plan Management')}>
            <Box
                component="section"
                id="current-plan"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div>
                    <Typography gutterBottom variant="h6">
                        {gettext('Manage your plan')}
                    </Typography>
                    {latestSubscription && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                {gettext('Your account is currently on a')}
                            </Typography>
                            <Chip
                                variant="outlined"
                                color="primary"
                                size="small"
                                label={latestSubscription.plan_name}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {interpolate(gettext('plan that costs $%s/month.'), [15])}
                            </Typography>
                        </Box>
                    )}
                </div>
                <PlanButton variant="outlined" onClick={handleSwitchPlan}>
                    {gettext('Switch Plan')}
                </PlanButton>
            </Box>

            <Divider />

            <Box
                component="section"
                id="cancel-plan"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box>
                    <Typography gutterBottom variant="h6">
                        {gettext('Cancel your plan')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {gettext(
                            "If you choose to cancel your plan, you'll still have access until the end of your billing period."
                        )}
                    </Typography>
                </Box>
                <PlanButton variant="outlined" color="error" onClick={handleCancelPlan}>
                    {gettext('Cancel plan')}
                </PlanButton>
            </Box>
        </SettingsSection>
    )
}

function ManageBilling() {
    const { data } = useSubscriptions()

    const handleUpdatePayment = () => {
        console.log('update payment')
    }

    const handleViewInvoices = () => {
        console.log('view invoices')
    }

    const getBillingSubHeader = () => {
        const activeSubscription = data?.find((sub) =>
            ['active', 'trialing', 'past_due'].includes(sub.status)
        )
        if (!activeSubscription?.end_date) {
            return undefined
        }

        const nextBillingDate = activeSubscription.end_date
        return interpolate(
            gettext(
                "Your monthly subscription renews on <%(tag)s>%(nextBillingDate)s</%(tag)s>. We'll notify you when your next subscription renews."
            ),
            {
                tag: 'strong',
                nextBillingDate: formatDate(nextBillingDate, {
                    format: 'MMMM D, YYYY',
                }),
            },
            true
        )
    }

    return (
        <SettingsSection header={gettext('Billing')} subHeader={getBillingSubHeader()}>
            <Stack spacing={4}>
                <Stack component="section" id="payment-details" spacing={2}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box>
                            <Typography gutterBottom variant="h6">
                                {gettext('Payment details')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {gettext('Update the payment method and billing address.')}
                            </Typography>
                        </Box>
                        <BillingButton
                            variant="outlined"
                            endIcon={<LaunchIcon />}
                            onClick={handleUpdatePayment}
                        >
                            {gettext('Update payment')}
                        </BillingButton>
                    </Box>
                </Stack>

                <Divider />

                <Stack component="section" id="invoices" spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography gutterBottom variant="h6">
                                {gettext('Invoices')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {gettext('View all past invoices.')}
                            </Typography>
                        </Box>
                        <BillingButton
                            variant="outlined"
                            endIcon={<LaunchIcon />}
                            onClick={handleViewInvoices}
                        >
                            {gettext('View Invoices')}
                        </BillingButton>
                    </Box>
                </Stack>
            </Stack>
        </SettingsSection>
    )
}

export function Billing() {
    return (
        <SettingsLayout>
            <ManagePlan />
            <ManageBilling />
        </SettingsLayout>
    )
}
